import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { useAddNoteTopic, useUploadMedia } from '@/hooks/api/dashboard'
import { Ego } from '@/types'
import { uploadMedia } from '@/utils/helper'
import { fireAlertToast, getAlertCopy } from '@/design-system/alerts'

/**
 * Matches babylon_meshdom AlbumInput: upload each file to ego/upload-object,
 * then attach ALL egos to ONE note via topicNoteUpdate (action: add).
 * Likes/comments are note-scoped — that is the intended API model.
 */
export type AlbumUploadOptions = {
  /** Override hook albumId — needed when creating then uploading in one turn */
  albumId?: string
  /** Per-file progress 0–100 as each file finishes processing/upload */
  onItemProgress?: (index: number, progress: number) => void
  /** Suppress success toast (create-flow navigates away) */
  silent?: boolean
  /**
   * Resize ceiling (long edge, px) for this batch — caller computes once from
   * `album.fullResolutionUploads && isFeatureAvailable(FULL_RESOLUTION_UPLOAD)`
   * (see UPLOAD_RESIZE_DIM). Omit for today's default (1000px).
   */
  maxDim?: number
}

export function useAlbumUpload(albumId: string | undefined, onDone?: () => void) {
  const uploadFile = useUploadMedia()
  const addNote = useAddNoteTopic()
  const [progress, setProgress] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)

  const uploadFiles = useCallback(
    async (files: File[], opts?: AlbumUploadOptions): Promise<Ego[]> => {
      const targetId = opts?.albumId ?? albumId
      if (!targetId || targetId.startsWith('new-')) {
        fireAlertToast('album.saveFirst')
        return []
      }
      if (!files.length) return []

      setUploading(true)
      setProgress(0)
      const egoItems: Ego[] = []

      try {
        for (let i = 0; i < files.length; i++) {
          opts?.onItemProgress?.(i, 12)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const resized: any = await uploadMedia(files[i], opts?.maxDim)
          if (resized?.isError) {
            fireAlertToast('photo.processFailed')
            opts?.onItemProgress?.(i, 0)
            continue
          }
          opts?.onItemProgress?.(i, 55)
          const photo = await uploadFile.mutateAsync(resized)
          egoItems.push({
            sequenceID: `${i}`,
            egoID: photo.egoID,
            photoFullURL: photo.egoImageURL,
            photoAlbumURL: photo.egoAlbumURL,
          })
          opts?.onItemProgress?.(i, 100)
          setProgress(Math.round(((i + 1) / files.length) * 90))
        }

        if (egoItems.length === 0) {
          fireAlertToast('photo.noneUploaded')
          return []
        }

        await addNote.mutateAsync({
          topicID: targetId,
          egoItems,
          noteName: 'untitled',
          noteText: 'placeholder',
        })
        setProgress(100)
        if (!opts?.silent) {
          if (egoItems.length === 1) {
            fireAlertToast('photo.added')
          } else {
            fireAlertToast('photo.addedPlural', { title: `${egoItems.length} photos added` })
          }
        }
        onDone?.()
        return egoItems
      } catch (e) {
        toast.error(e instanceof Error ? e.message : getAlertCopy('photo.uploadFailed').title)
        throw e
      } finally {
        setUploading(false)
        setProgress(null)
      }
    },
    [albumId, uploadFile, addNote, onDone],
  )

  return { uploadFiles, uploading, progress }
}
